import { useMemoizedFn } from 'ahooks';
import { RcFile } from 'antd/lib/upload';
import { useUploadResource } from 'hooks/business/useUploadResource';
import produce from 'immer';
import { isNil } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { atom, useAtomValue } from 'jotai';
import { v4 as uuidV4 } from 'uuid';
import { useAtomCallback } from 'jotai/utils';

export interface MsgAttachmentInfo {
  id: string;
  storeId: Conversation.Conversation['storeId'];
  status: 'uploading' | 'success' | 'error';
  data: Conversation.MsgAttachmentInfo;
}

type MsgAttachments = Record<Conversation.Conversation['conversationId'], MsgAttachmentInfo[]>;

const msgAttachmentsState = atom<MsgAttachments>({});

export const useMsgAttachmentList = (
  conversationId: Conversation.Conversation['conversationId'],
) => {
  const msgAttachments = useAtomValue(msgAttachmentsState);
  return useMemo(() => msgAttachments[conversationId] ?? [], [msgAttachments, conversationId]);
};

export const useSetMsgAttachments = () => {
  return useAtomCallback(
    useCallback(
      async (
        _,
        set,
        conversationId: Conversation.Conversation['conversationId'],
        info: MsgAttachmentInfo,
      ) => {
        set(
          msgAttachmentsState,
          produce((draft) => {
            const list = draft[conversationId] ?? [];
            const index = list.findIndex((item) => item.id === info.id);
            if (index > -1) {
              list[index] = info;
            } else {
              list.push(info);
            }
            draft[conversationId] = [...list];
          }),
        );
      },
      [],
    ),
  );
};

export const useRemoveMsgAttachments = () => {
  return useAtomCallback(
    useCallback(
      async (_, set, conversationId: Conversation.Conversation['conversationId'], id: string) => {
        set(
          msgAttachmentsState,
          produce((draft) => {
            if (draft[conversationId]) {
              const index = draft[conversationId].findIndex((item) => item.id === id);
              if (index > -1) {
                draft[conversationId].splice(index, 1);
              }
            }
          }),
        );
      },
      [],
    ),
  );
};

export const useClearMsgAttachments = () => {
  return useAtomCallback(
    useCallback(async (_, set, conversationId: Conversation.Conversation['conversationId']) => {
      set(
        msgAttachmentsState,
        produce((draft) => {
          if (!isNil(draft[conversationId])) {
            delete draft[conversationId];
          }
        }),
      );
    }, []),
  );
};

export const useUploadAttachment = () => {
  const uploadResource = useUploadResource();
  const setMsgAttachments = useSetMsgAttachments();

  const uploadAttachment = useMemoizedFn(
    async (
      conversationId: Conversation.Conversation['conversationId'],
      storeId: Conversation.Conversation['storeId'],
      region: General.UpperCaseRegion,
      file: RcFile,
    ) => {
      const id = uuidV4();

      try {
        setMsgAttachments(conversationId, {
          id,
          storeId,
          status: 'uploading',
          data: {
            filename: file.name,
            originalFilename: file.name,
            size: `${file.size}`,
            type: file.type,
            url: '',
          },
        });
        const res = await uploadResource({
          file,
          storeId,
          region,
          fileType: 'IMAGE',
          reqSource: 'ATTACHMENT',
        });
        setMsgAttachments(conversationId, {
          id,
          storeId,
          status: 'success',
          data: {
            filename: res.url,
            originalFilename: file.name,
            size: `${file.size}`,
            type: file.type,
            url: res.localUrl!,
          },
        });
      } catch (err) {
        setMsgAttachments(conversationId, {
          id,
          storeId,
          status: 'error',
          data: {
            filename: '',
            originalFilename: file.name,
            size: `${file.size}`,
            type: file.type,
            url: '',
          },
        });
      }
    },
  );

  return uploadAttachment;
};
